import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useCourseInfo from '@api/hooks/course/useCourseInfo';
import useClassInfo from '@api/hooks/class/useClassInfo';
import useSimilarAndOtherCourses from '@api/hooks/class/useSimilarAndOtherCourses';
import useCheckFeatureLimit from '@cont/DeviceLimitation/useCheckFeatureLimit';
import useCourseClassPlayer from '@hooks/useCourseClassPlayer';
import { FeatureType } from 'types/features';
import { isUpcoming } from '@models/Item';
import { readCookie } from '@utils/cookies';
import getParamsFromURL from '@utils/getParamsFromURL';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { emptyObject } from '@constants/empty';
import { useIsMobile } from '@stores/AppCommonData';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getIsPlatformUnlocked } from '@api/selectors/auth';

const useLanding = () => {
  const { query } = useRouter();
  const { uid: classUID } = query;

  const { classInfo, mutate: mutateClassData } = useClassInfo(classUID);
  const { course: courseInfo, mutate: mutateCourseData } = useCourseInfo(
    classInfo.programme.uid
  );
  const { mutate: mutateSimilarCourses } = useSimilarAndOtherCourses(classUID);

  const isMobile = useIsMobile();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isPlatformUnlocked = useMyInfo(getIsPlatformUnlocked);
  const { isValidating: fetchingUserData } = useMyInfo();

  const { open: openClassPlayer } = useCourseClassPlayer();
  const handleClassItemClick = useCheckFeatureLimit(
    FeatureType.CLASS,
    (...args) => openClassPlayer(...args)
  );

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);

  useEffect(() => {
    if (!courseInfo.uid) {
      if (!readCookie('accessToken') && !fetchingUserData) {
        openLoginDrawer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      const [isSyllabusFeedParam, noAutoplayParam] = getParamsFromURL(
        window.location.href,
        ['isSyllabusFeed', 'noAutoplay']
      );

      const isFreeLesson = isSyllabusFeedParam === 'true';
      const noAutoplay = noAutoplayParam === 'true';

      const fetchAndCheckForPlayable = async () => {
        const [updatedClassInfo, updatedCourseInfo] = await Promise.all([
          mutateClassData(),
          mutateCourseData()
        ]);
        const { programme = emptyObject } = updatedCourseInfo || emptyObject;
        const { isSpecial = false } = programme;

        if (
          !isMobile &&
          !noAutoplay &&
          (!isSpecial ||
            (isSpecial && !isUpcoming(updatedClassInfo) && isPlatformUnlocked))
        ) {
          handleClassItemClick({
            lesson: updatedClassInfo,
            course: updatedCourseInfo,
            byPassCheck: isFreeLesson,
            isFreeLesson,
            forceFetch: isFreeLesson
          });
        }

        if (isSpecial) {
          mutateSimilarCourses();
        }
      };

      fetchAndCheckForPlayable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, classInfo.uid]);
};

export default useLanding;
